/**
 * USE-SECONDARY-GUARDS
 * The link between a primary question and its secondary questions is a critical one :
 * - adding a secondary question asks when it is displayed (QCM : an answer or always, scale : low or high note)
 * - deleting an answer that triggers secondary questions asks what to do with them first
 * Shared by the card and the list editors.
 */
import { ReactNode, useState } from "react"
import { store } from "@/index"
import { Question, TRIGGER_ALWAYS } from "@/redux/question.types"
import { Topic } from "@/redux/topic.types"
import { t } from "@/translate/t"
import { toast } from "react-toastify"
import { getChoicePropositionIssues } from "@/utils/choice-propositions.utils"
import { sessionEditUserOptions } from "@/redux/_session.actions"
import { userUpdateOptions } from "@/redux/user.actions"
import { topicAddQuestion, topicEditTrigger, topicRemoveProposition, topicRemoveQuestion } from "@/redux/topic.actions"
import SecondaryTriggerModal from "@/modals/secondary-trigger.modal"
import SecondaryDetachModal from "@/modals/secondary-detach.modal"

//Scroll to the secondary question just added (the last one of the active topic)
function scrollToNewQuestion() {
  setTimeout(() => {
    const questions = store.getState().topic.active.Questions
    const added = questions[questions.length - 1]
    document.getElementById("question-" + added?.id)?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, 200)
}

export function useSecondaryGuards(
  language: string,
  isSupervisor: boolean
): {
  modals: ReactNode
  requestAddSecondary: Function
  requestRemoveProposition: Function
} {
  const [pendingAdd, setPendingAdd] = useState<{
    topic: Topic
    fallbackTriggerValue: string | null
    initialTrigger: string | null
  } | null>(null)
  const [pendingRemove, setPendingRemove] = useState<{
    topic: Topic
    questionId: string
    propositionId: string
  } | null>(null)

  //Add a secondary question : ask for the trigger when the primary is a QCM or a scale
  //(open question : always asked, battery : no trigger possible)
  function requestAddSecondary(
    topic: Topic,
    fallbackTriggerValue: string | null = null,
    fromQuestion: Question | null = null //Secondary question the user clicked + on : its trigger is proposed by default
  ) {
    const primary = topic.questionPrimary
    const needsTrigger =
      primary && !primary.options.multipleItems && (primary.type === "choice" || primary.type === "scale")

    //A comment question cannot have secondary questions
    if (primary && primary.type === "open") {
      toast(t("question_open_secondary_blocked"), { type: "error" })
      return
    }

    //Two identical answers : the trigger could not be chosen, they have to be fixed first
    const duplicate = primary ? getChoicePropositionIssues(primary, [language]).duplicates[0] : undefined
    if (needsTrigger && duplicate) {
      toast(t("secondary_trigger_duplicate_answers", { label: duplicate.label }), { type: "error" })
      return
    }

    if (needsTrigger && primary && !store.getState()._session.userOptions.editorAskSecondaryTrigger) {
      addWithDefaultTrigger(topic, primary, fromQuestion)
    } else if (needsTrigger) {
      setPendingAdd({
        topic,
        fallbackTriggerValue,
        initialTrigger: fromQuestion && fromQuestion.id !== primary?.id ? fromQuestion.getTrigger(primary) : null
      })
    } else {
      store.dispatch(topicAddQuestion(isSupervisor, false, fallbackTriggerValue))
      scrollToNewQuestion()
    }
  }

  //The user does not want to be asked : same trigger as the question the + was clicked on, otherwise the historical
  //one (scale : low note, QCM : always) and a toast tells where to change it
  function addWithDefaultTrigger(topic: Topic, primary: Question, fromQuestion: Question | null) {
    const isChoice = primary.type === "choice"
    let trigger = fromQuestion && fromQuestion.id !== primary.id ? fromQuestion.getTrigger(primary) : null
    const isValid = isChoice
      ? trigger === TRIGGER_ALWAYS || primary.Propositions.some((x) => x.id === trigger)
      : trigger === "<" || trigger === ">"
    if (!isValid) trigger = isChoice ? TRIGGER_ALWAYS : "<"

    if (isChoice) {
      store.dispatch(topicAddQuestion(isSupervisor, false, "<", trigger === TRIGGER_ALWAYS ? null : trigger))
    } else {
      store.dispatch(topicAddQuestion(isSupervisor, false, trigger, null))
    }
    scrollToNewQuestion()
    toast(
      t(
        !isChoice
          ? trigger === "<"
            ? "secondary_trigger_added_low"
            : "secondary_trigger_added_high"
          : trigger === TRIGGER_ALWAYS
            ? "secondary_trigger_added_always"
            : "secondary_trigger_added_answer"
      ),
      { type: "info" }
    )
  }

  //Do not ask again (can be changed in the options of the editor)
  function disableAsking() {
    store.dispatch(sessionEditUserOptions("editorAskSecondaryTrigger", false))
    const session = store.getState()._session
    if (session.interfaceType !== "SUPERVISOR") {
      store.dispatch(userUpdateOptions(session.userId, session.userOptions))
    }
  }

  //Secondary questions triggered by an answer of the primary question
  function getDependents(topic: Topic, questionId: string, propositionId: string) {
    return questionId === topic.questionPrimaryId
      ? topic.questionsSecondary.filter((x) => x.QuestionsTopic.triggerChoice === propositionId)
      : []
  }

  //Remove an answer : ask what to do with the secondary questions it triggers
  function requestRemoveProposition(topic: Topic, questionId: string, propositionId: string) {
    if (getDependents(topic, questionId, propositionId).length > 0) {
      setPendingRemove({ topic, questionId, propositionId })
    } else {
      store.dispatch(topicRemoveProposition(questionId, propositionId))
    }
  }

  function confirmRemove(mode: "detach" | "delete") {
    if (!pendingRemove) return
    const { topic, questionId, propositionId } = pendingRemove
    getDependents(topic, questionId, propositionId).forEach((question) => {
      if (mode === "delete") {
        store.dispatch(topicRemoveQuestion(question.id, topic.id))
      } else {
        store.dispatch(topicEditTrigger(question.id, "triggerChoice", null))
      }
    })
    store.dispatch(topicRemoveProposition(questionId, propositionId))
    setPendingRemove(null)
  }

  const removedProposition = pendingRemove
    ? pendingRemove.topic.Questions.find((x) => x.id === pendingRemove.questionId)?.Propositions.find(
        (x) => x.id === pendingRemove.propositionId
      )
    : null

  const modals = (
    <>
      {pendingAdd && pendingAdd.topic.questionPrimary && (
        <SecondaryTriggerModal
          initialTrigger={pendingAdd.initialTrigger}
          language={language}
          primary={pendingAdd.topic.questionPrimary}
          onClose={() => setPendingAdd(null)}
          onValidate={(trigger, dontAsk) => {
            if (dontAsk) disableAsking()
            store.dispatch(topicAddQuestion(isSupervisor, false, trigger.triggerValue, trigger.triggerChoice))
            scrollToNewQuestion()
            setPendingAdd(null)
          }}
        />
      )}

      {pendingRemove && (
        <SecondaryDetachModal
          answer={removedProposition?.name[language] || t("question_new")}
          dependents={getDependents(pendingRemove.topic, pendingRemove.questionId, pendingRemove.propositionId).map(
            (x) => x.name[language] || t("question_new")
          )}
          onClose={() => setPendingRemove(null)}
          onValidate={confirmRemove}
        />
      )}
    </>
  )

  return { modals, requestAddSecondary, requestRemoveProposition }
}
